import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

export interface MoveNodeTargetDay {
  date: string;
  dateLabel: string;
}

export interface MoveNodePlan {
  /** Ce qui se déplace : un bloc de WOD, un bloc de catégorie, ou une heat. */
  kind: 'wod' | 'category' | 'heat';
  label: string;
  sourceDate: string;
  heatCount: number;
  teamCount: number;
}

interface Props {
  plan: MoveNodePlan | null;
  days: MoveNodeTargetDay[];
  onCancel: () => void;
  onConfirm: (date: string) => void;
}

/**
 * Envoyer un bloc — ou une heat — sur un autre jour.
 *
 * Cette fenêtre ne double pas le glisser-déposer, elle le complète : le
 * planning se lit une journée à la fois, et on ne peut pas glisser vers un
 * onglet masqué. C'est donc le SEUL chemin pour changer de jour.
 *
 * Elle ne demande que le jour. Une position d'arrivée n'aurait plus de sens
 * depuis qu'un même WOD peut apparaître plusieurs fois dans une journée : le
 * bloc atterrit à la fin, et se replace ensuite au glisser, là où on voit ce
 * qu'on fait.
 */
const MoveNodeDialog: React.FC<Props> = ({ plan, days, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const [date, setDate] = React.useState('');

  // Repartir du jour d'origine à chaque ouverture.
  React.useEffect(() => { if (plan) setDate(plan.sourceDate); }, [plan]);

  if (!plan) return null;

  return (
    <Dialog open onOpenChange={o => { if (!o) onCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('heats.moveNode.title')}</DialogTitle>
          <DialogDescription>
            {plan.label}
            {' · '}
            {t('heats.moveNode.impact', { heats: plan.heatCount, teams: plan.teamCount })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>{t('heats.moveNode.day')}</Label>
            <Select value={date} onValueChange={setDate}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {days.map(d => (
                  <SelectItem key={d.date} value={d.date}>
                    <span className="capitalize">{d.dateLabel}</span>
                    {d.date === plan.sourceDate && ` — ${t('heats.moveNode.currentDay')}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-muted-foreground">{t('heats.moveNode.endOfDayNotice')}</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button onClick={() => onConfirm(date)} disabled={date === plan.sourceDate}>
            {t('heats.moveNode.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MoveNodeDialog;
